import {
  isSpirometryFailure,
  pickSpirometryResult,
  SPIROMETRY_PREDICTION_SOURCES,
} from "./spirometry";
import { buildAdditionalRespiratoryMetricsFields } from "./additionalRespiratoryMetrics";
import { isFailedCheck } from "./resultQuality";

export function getSpiroQualityMessage(
  { signalQuality, spirometryQuality, zeroedMetrics },
  t,
) {
  if (
    !isSpirometryFailure({
      signalQuality,
      spirometryQuality,
      zeroedMetrics,
    })
  ) {
    return null;
  }

  const signalFailed =
    typeof signalQuality === "string" && signalQuality !== "pass";
  const spirometryFailed =
    typeof spirometryQuality === "string" && spirometryQuality !== "pass";

  if (signalFailed && spirometryFailed) {
    return {
      title: t(
        "result.spiro.recordingQualityFailTitle",
        "Recording quality needs attention.",
      ),
      subtitle: t("result.spiro.qualityFailSubtitle", "Please try again."),
    };
  }

  if (spirometryFailed) {
    return {
      title: t(
        "result.spiro.manoeuvreQualityFailTitle",
        "Spirometry manoeuvre quality needs attention.",
      ),
      subtitle: t("result.spiro.qualityFailSubtitle", "Please try again."),
    };
  }

  if (zeroedMetrics) {
    return {
      title: t(
        "result.spiro.overallQualityFailTitle",
        "Recording quality needs attention.",
      ),
      subtitle: t("result.spiro.qualityFailSubtitle", "Please try again."),
    };
  }

  return {
    title: t("result.spiro.qualityFailTitle", "Low signal quality."),
    subtitle: t("result.spiro.qualityFailSubtitle", "Please try again."),
  };
}

export function getSpirometryQualityHintKeys(checks) {
  if (!checks || typeof checks !== "object") return [];

  const orderedChecks = [
    "fast_blow_effort",
    "end_plateau",
    "spiro_amplitude",
    "segment_detected",
    "spiro_location",
  ];

  return orderedChecks.filter((key) => isFailedCheck(checks?.[key]));
}

export function buildSpirometryAdditionalMetricsRecord(
  additionalMetrics,
  rr,
  signalQuality,
  spirometryQuality,
) {
  return {
    meanExhaleInhaleAreaRatio: additionalMetrics?.meanExhaleInhaleAreaRatio,
    rr_min: rr?.min,
    rr_max: rr?.max,
    rr_mean: rr?.mean,
    rr_median: rr?.median,
    min_breath_duration_seconds: additionalMetrics?.minBreathDurationSeconds,
    max_breath_duration_seconds: additionalMetrics?.maxBreathDurationSeconds,
    mean_breath_duration_seconds: additionalMetrics?.meanBreathDurationSeconds,
    breath_count: rr?.breathCount,
    signal_quality: signalQuality,
    spirometry_quality: spirometryQuality,
  };
}

export function buildSpirometryAdditionalMetricsFields(t) {
  return buildAdditionalRespiratoryMetricsFields(t);
}

export function isSpirometryResultReady(
  data,
  { predictionSource = SPIROMETRY_PREDICTION_SOURCES.LUNGDX } = {},
) {
  const {
    fev1,
    pef,
    rr,
    signalQuality,
    spirometryQuality,
    zeroedMetrics,
    additionalMetrics,
  } = pickSpirometryResult(data, { predictionSource });

  return (
    fev1 != null ||
    pef != null ||
    rr?.average != null ||
    rr?.mean != null ||
    additionalMetrics?.meanExhaleInhaleAreaRatio != null ||
    isSpirometryFailure({ signalQuality, spirometryQuality, zeroedMetrics })
  );
}
